const EXPENSE_RATE = 0.05; // 諸経費率：販売価格の5%

/**
 * YYYYMMDD形式の築年月文字列から現在時点の築年数を計算する。
 *
 * 竣工月が判明している場合（MM !== "00"）は月単位精度で計算し、
 * REINS等の外部サイトと一致する築年数を返す。
 * 竣工月が不明（"00"等）の場合は年のみで計算する。
 *
 * @param builtYear YYYYMMDD形式の文字列（例: "19831200"）
 * @param now       テスト用の現在日時（省略時は実際の現在時刻）
 * @returns 築年数（0以上の整数）。年が取得不能な場合は null
 */
export function calcBuildingAge(
  builtYear: string | null | undefined,
  now?: Date,
): number | null {
  if (!builtYear) return null;

  const yearNum = parseInt(builtYear.toString().slice(0, 4), 10);
  if (isNaN(yearNum) || yearNum < 1900) return null;

  const current = now ?? new Date();
  const currentYear  = current.getFullYear();
  const currentMonth = current.getMonth() + 1; // 1-indexed

  if (yearNum > currentYear) return null;

  const monthNum = parseInt(builtYear.toString().slice(4, 6), 10);
  const hasMonth = !isNaN(monthNum) && monthNum >= 1 && monthNum <= 12;

  const age = hasMonth && currentMonth < monthNum
    ? currentYear - yearNum - 1
    : currentYear - yearNum;

  return Math.max(0, age);
}

/**
 * シミュレーション計算
 * 想定家賃・楽待利回り・販売価格から年間賃料・売却費・諸経費・利益を算出する
 */
export function calcSimulation(
  rent: number | null,
  yieldRate: number | null,
  price: number | null,
): { annualRent: number; salePrice: number; expenses: number; profit: number } | null {
  if (!rent || !yieldRate || !price || yieldRate === 0 || price === 0) return null;
  const annualRent = rent * 12;
  const salePrice = annualRent / (yieldRate / 100);
  const expenses = price * EXPENSE_RATE;
  const profit = salePrice - price - expenses;
  return { annualRent, salePrice, expenses, profit };
}

/**
 * 実際利回りを計算する
 * (想定年間賃料 - 年間管理費修繕積立金) / 販売価格 × 100
 */
export function calcActualYield(
  rent: number | null,
  price: number | null,
  managementFee: number | null,
  reserveFund: number | null,
  otherCost: number | null = null,
): number | null {
  if (!rent || !price || price === 0) return null;
  const annualRent = rent * 12;                                                  // 万円/年
  const annualCostMan = ((managementFee || 0) + (reserveFund || 0) + (otherCost || 0)) * 12 / 10000; // 円→万円変換
  return ((annualRent - annualCostMan) / price) * 100;
}
